// English — professional translations (not literal machine translation)
export const en = {
  meta: {
    homeTitle: "Bula Telecoms | Fibre, Wireless, Satellite and IP Transit in the DRC",
    homeDesc: "Bula Telecoms delivers fibre, wireless, satellite, IP Transit and enterprise network solutions in the Democratic Republic of the Congo and across Africa.",
  },
  nav: {
    home: "Home", about: "About", solutions: "Solutions", sectors: "Sectors",
    infrastructure: "Infrastructure", impact: "Impact", news: "News", contact: "Contact",
    coverage: "Check coverage", quote: "Request a quote", talkExpert: "Talk to an expert",
    careers: "Careers", partners: "Partners", rural: "Rural connectivity",
    legal: "Legal notice", privacy: "Privacy", cookies: "Cookies",
    menu: "Menu", close: "Close",
  },
  hero: {
    eyebrow: "BULA TELECOMS SA — CONNECTIVITY & DIGITAL INFRASTRUCTURE",
    title1: "Connecting the DRC.",
    title2: "Powering Africa.",
    sub: "Bula Telecoms deploys fibre, wireless and satellite connectivity solutions designed for households, businesses, operators and public institutions. Our ambition is simple: to make the Internet more reliable, more accessible and more useful for Africa's development.",
    strip: ["FTTH/FTTB fibre", "Fixed wireless Internet", "Satellite", "IP Transit & DIA", "Enterprise solutions"],
  },
  credibility: {
    items: [
      { title: "3 access technologies", sub: "Fibre, wireless and satellite" },
      { title: "__CAPACITY__", sub: "__CAPACITY_NOTE__" },
      { title: "Multi-segment connectivity", sub: "Residential, enterprise, operator and public sector" },
      { title: "Pan-African vision", sub: "The DRC as a starting point" },
    ],
  },
  mission: {
    heading: "Internet should not be a luxury.",
    copy: "Our mission is to provide a growing share of the Congolese population with reliable, affordable Internet access. We want to connect households, strengthen business competitiveness, modernise institutions and give communities the means to participate fully in the digital economy.",
    highlight: "Our success rests on three pillars: unwavering commitment, solid expertise and an innovative commercial approach focused on the market's real needs.",
    cta: "Discover Bula Telecoms",
  },
  solutionsHome: {
    heading: "Connectivity suited to every reality",
    intro: "No single technology can answer every need in the DRC and Africa. Bula Telecoms combines fibre optics, wireless links, satellite and network infrastructure to deploy the most relevant solution for each site.",
    cta: "Explore all our solutions",
  },
  network: {
    heading: "An architecture designed for resilience",
    copy: "Bula Telecoms relies on a diversified infrastructure combining points of presence, data centres, fibre-optic routes, international backhaul and carrier-grade interconnection. This approach improves availability, reduces latency and supports rapid deployments in the DRC and other African markets.",
    highlights: [
      "Multi-route architecture.", "Points of presence and data centres.",
      "International connectivity.", "Redundancy of critical links.",
      "Peering, traffic exchange and route optimisation.",
      "Solutions designed for business-continuity requirements.",
    ],
    mapLabel: "Illustration of our network architecture",
    cta: "Discover our infrastructure",
  },
  segments: { heading: "Solutions for every market" },
  rural: {
    heading: "No community should remain off the network.",
    copy: "Bula Telecoms is committed to extending Internet access to rural and underserved areas of the Democratic Republic of the Congo and Africa. Our hybrid model combines fibre, wireless technologies and satellite to connect communities, schools, health centres, agricultural businesses, local administrations and essential services.",
    support: "Our priority is to connect ordinary Congolese people and, more broadly, to enable Africans to participate in the digital economy.",
    cta: "Discover our vision for rural areas",
  },
  why: {
    heading: "Why Bula Telecoms?",
    items: [
      { title: "A network designed for reliability", copy: "A redundant architecture and a combination of technologies that adapt each deployment to conditions on the ground." },
      { title: "A multi-technology approach", copy: "Fibre, wireless and satellite within a single portfolio, covering urban, peri-urban and remote areas." },
      { title: "Tailor-made solutions", copy: "Every network is designed around the client's objectives, sites, operational constraints and performance requirements." },
      { title: "Customer-focused service", copy: "An approach built on listening, responsiveness, transparency and long-term support." },
      { title: "Local expertise, continental ambition", copy: "Knowledge of African environments combined with international technical and operational standards." },
    ],
  },
  ecosystem: { label: "Network and infrastructure ecosystem" },
  finalCta: {
    heading: "Let's build the connection your project needs.",
    copy: "Whether you need to connect a home, a business, several branches, an institution or an operator network, our team will help you identify the most suitable architecture.",
  },
  footer: {
    mission: "Reliable, affordable and resilient connectivity for households, businesses, operators and public institutions.",
    solutions: "Solutions", sectors: "Sectors", company: "Company", contact: "Contact",
    newsletter: "Stay informed", newsletterPlaceholder: "Your email address",
    newsletterBtn: "Subscribe", legal: "Legal",
    rights: "© {year} Bula Telecoms SA. All rights reserved.",
    tagline: "Designed in Africa to connect Africa.",
  },
  forms: {
    required: "This field is required",
    invalidEmail: "Invalid email address",
    consentRequired: "Please accept the privacy policy",
    consentLabel: "I agree that my data will be processed in accordance with the privacy policy.",
    securityCheck: "Security check",
    sending: "Sending…", submit: "Send",
    success: "Your request has been received.",
    error: "Something went wrong. Please try again.",
    privacyNote: "The information collected is used by Bula Telecoms to process your request. See our privacy policy.",
  },
  coverage: {
    title: "Check coverage",
    subtitle: "Tell us your address: our team will assess availability and the technical options for your site.",
    successMsg: "Your coverage request has been received. Our team will assess availability and the technical options for your address.",
    fields: {
      customerType: "Customer type", fullName: "Full name", company: "Company or organisation",
      phone: "Telephone", email: "Email", province: "Province", city: "City",
      commune: "Commune or district", address: "Full installation address",
      mapPin: "Map pin or coordinates (optional)", buildingType: "Building type",
      usersOrSites: "Number of users or sites", service: "Required service",
      capacity: "Desired capacity", contactMethod: "Preferred contact method",
      details: "Additional details",
    },
    customerTypes: ["Individual", "Residence or building", "Business", "Operator or ISP", "Government or public institution", "Property developer", "Reseller", "Other"],
    buildingTypes: ["Single house", "Apartment", "Multi-dwelling building", "Office", "Industrial site", "Campus", "Other"],
    contactMethods: ["Phone", "Email", "WhatsApp"],
    submit: "Send my request",
  },
  quote: {
    title: "Request a quote",
    subtitle: "A guided 5-step journey to help us design the solution best suited to your project.",
    steps: ["Customer profile", "Required service", "Site & coverage", "Technical requirements", "Contact & submission"],
    step1: { customerType: "Customer type", company: "Company or organisation", sector: "Industry" },
    step2: { service: "Primary service", details: "Describe your need" },
    step3: { province: "Province", city: "City", address: "Site address", sites: "Number of sites" },
    step4: {
      bandwidth: "Required bandwidth", connectionType: "Connection type",
      dedicated: "Dedicated", shared: "Shared", redundancy: "Redundancy requirement",
      sla: "SLA requirement", publicIp: "Public IP requirement",
      existingProvider: "Current provider", targetDate: "Target deployment date",
      description: "Project description", attachment: "Attachment (optional)",
    },
    step5: { fullName: "Full name", role: "Job title", email: "Email", phone: "Telephone", contactMethod: "Preferred contact method" },
    conditional: {
      fibre: "Approximate distance or buildings to serve",
      wireless: "Clear line of sight between sites?",
      satellite: "Area of operation and access constraints",
      iptransit: "Current capacity and desired ports",
      government: "Institution, project scope and stakeholders",
      civil: "Type of works (trenching, aerial, chambers…) and estimated distance",
    },
    back: "Back", next: "Continue", submit: "Send quote request",
    successMsg: "Your quote request has been received. Our team will review your requirements and get back to you.",
  },
  contact: {
    title: "Contact",
    subtitle: "A question, a project, a partnership? Our team will answer.",
    departments: {
      general: "General enquiries", sales: "Sales", enterprise: "Enterprise & wholesale",
      government: "Government projects", partnerships: "Partnerships", careers: "Careers", media: "Media & press",
    },
    fields: { department: "Department", name: "Full name", email: "Email", phone: "Telephone", subject: "Subject", message: "Message" },
    submit: "Send message",
    successMsg: "Your message has been received. Our team will reply as soon as possible.",
    hq: "Headquarters — Kinshasa", office: "Office — Cape Town", hours: "Opening hours",
    emergency: "Network support",
  },
  careers: {
    title: "Careers",
    heading: "Join a team that builds the digital infrastructure of tomorrow.",
    intro: "At Bula Telecoms, we design and deploy the networks that connect the DRC and Africa. Joining our teams means playing a concrete part in the continent's digital transformation.",
    openPositions: "Open positions",
    noPositions: "No positions are published at the moment. You can still send us a spontaneous application below.",
    apply: "Apply",
    spontaneous: "Spontaneous application",
    fields: { name: "Full name", email: "Email", phone: "Telephone", position: "Position sought", location: "Location", linkedin: "LinkedIn profile", cv: "CV (PDF)", cover: "Cover note" },
    submit: "Send my application",
    successMsg: "Your application has been received. Our team will review it carefully.",
  },
  news: { title: "News & insights", readMore: "Read the article", back: "Back to news", empty: "News will be published soon." },
  about: {
    heading: "An African company built to connect the future",
    missionTitle: "Our mission",
    mission: "To provide reliable, affordable connectivity that serves development, so that the Internet becomes an essential infrastructure accessible to as many people as possible.",
    visionTitle: "Our vision",
    vision: "To lastingly increase Internet access penetration in the Democratic Republic of the Congo, then extend that transformation across the African continent.",
    vision2: "Through connectivity, we want to strengthen the autonomy of young people, families, entrepreneurs, businesses and institutions.",
    valuesTitle: "Our values",
    values: ["Commitment", "Excellence", "Innovation", "Accessibility", "Integrity", "Customer focus", "Lasting impact"],
    timelineTitle: "Our journey",
    timeline: [
      { year: "2017", title: "South Africa", text: "Bula Telecoms is founded." },
      { year: "2019", title: "Democratic Republic of the Congo", text: "Establishment and growth of operations in the DRC." },
      { year: "Today", title: "African expansion", text: "Development of a multi-technology portfolio and an African expansion strategy." },
    ],
    corporateTitle: "Corporate information",
    corporate: [
      "Downloadable company profile",
      "Governance and compliance: Bula Telecoms SA is registered in the Democratic Republic of the Congo and applies sound governance, regulatory compliance and responsible data-management practices.",
      "Geographic strategy: the DRC as an anchor market, followed by gradual expansion into other African markets.",
    ],
    downloadProfile: "Download the company profile",
    partnershipCta: "Propose a partnership",
    mediaContact: "Media & press contact",
  },
  infrastructure: {
    heading: "Infrastructure designed for performance and continuity",
    intro: "Our network combines points of presence, data-centre interconnection, carrier-grade backhaul, international routes, fibre rings, wireless access and satellite redundancy.",
    items: [
      { title: "Points of presence", text: "Strategic points of presence bringing the network closer to users." },
      { title: "Data-centre interconnection", text: "Hosting and interconnection within carrier-grade environments." },
      { title: "International backhaul", text: "Diversified international routes securing capacity and availability." },
      { title: "Fibre-optic rings", text: "Ring architectures for resilient metropolitan routes." },
      { title: "Wireless access", text: "P2P and P2MP links for fast, flexible distribution." },
      { title: "Satellite redundancy", text: "Satellite backup for critical sites and isolated areas." },
      { title: "Wholesale capacity", text: "Scalable wholesale capacity for operators and ISPs." },
      { title: "Peering & traffic exchange", text: "Route optimisation and traffic exchange to reduce latency." },
      { title: "Network monitoring", text: "Continuous monitoring to anticipate incidents and protect service continuity." },
    ],
    mapLabel: "Illustration of our network architecture",
  },
  ruralPage: {
    heading: "Connecting the territories that traditional networks cannot reach alone",
    p1: "The digital divide is not just about the absence of signal. It affects education, health, entrepreneurship, agriculture, public services and economic participation.",
    p2: "Our rural connectivity model combines the technologies available according to the terrain: fibre for structural routes, wireless access for local distribution and satellite for isolated areas or rapid deployments.",
    impactTitle: "Impact areas",
    impacts: ["Education", "Health", "Agriculture", "Public administration", "Financial inclusion", "Entrepreneurship", "Security", "Community communication"],
    cta: "Propose a rural connectivity project",
  },
  partnersPage: {
    heading: "Partners & ecosystem",
    intro: "Bula Telecoms works with an ecosystem of infrastructure, connectivity and technology organisations. Every logo displayed is shown with the approval of the organisation concerned.",
    label: "Network and infrastructure ecosystem",
    become: "Become a partner",
    note: "The organisations shown are neither investors nor clients unless explicitly stated.",
  },
  solutionCommon: {
    outcome: "Outcome for your organisation", overview: "Technical overview", useCases: "Use cases",
    segments: "Relevant segments", benefits: "Key benefits", process: "Deployment process",
    faq: "Frequently asked questions", cta: "Request a quote", related: "Related solutions",
    processSteps: ["Requirement discussion and qualification", "Technical feasibility study", "Architecture design", "Deployment and commissioning", "Monitoring and ongoing support"],
    availability: "Availability subject to feasibility study, coverage, regulation and contractual conditions.",
  },
  legalPages: {
    legal: {
      title: "Legal notice",
      sections: [
        { h: "Site publisher", p: "Bula Telecoms SA\nQuantum Building, 2nd Floor, Apartment A\n81/77 Boulevard du 30 Juin, Kinshasa–Gombe\nDemocratic Republic of the Congo\nTel: +243 904 444 213\nEmail: info@bulatelecoms.com" },
        { h: "Registration", p: "RCCM: RCCM/19-B-00673\nID. NAT.: O1-730-N45580L" },
        { h: "Secondary office", p: "No. 2 Ixia Street, Unit 5, Milpark, Milnerton 7441, Cape Town, South Africa\nTel: +27 87 820 5053" },
        { h: "Intellectual property", p: "All site content (text, visuals, logo) is the property of Bula Telecoms SA unless stated otherwise. Any reproduction without written authorisation is prohibited." },
      ],
    },
    privacy: {
      title: "Privacy policy",
      sections: [
        { h: "Data we collect", p: "We collect the data you submit through our forms (identity, contact details, installation address, technical requirements) in order to process your coverage, quote, contact, application or newsletter requests." },
        { h: "Purposes and legal basis", p: "This processing is based on your consent and on pre-contractual measures. It is used to answer your requests, manage the business relationship and, with your agreement, send you communications." },
        { h: "Retention", p: "Data is kept for the time needed to process your request, then according to the retention periods we configure (leads and messages are retained for a limited period, configurable in our administration area)." },
        { h: "Your rights", p: "You have the right to access, rectify, object to and delete your data. To exercise these rights, write to info@bulatelecoms.com." },
        { h: "Security", p: "We apply appropriate technical and organisational measures: restricted access, audit logging, spam protection and server-side validation." },
      ],
    },
    cookies: {
      title: "Cookie policy",
      sections: [
        { h: "Essential cookies", p: "The site uses only cookies and technical storage necessary for its operation (language preference, administration session, consent preferences)." },
        { h: "Non-essential cookies", p: "No analytics or marketing cookies are set without your prior consent. You can change your preferences at any time." },
        { h: "Manage your preferences", p: "Use the preferences panel available via the \"Cookies\" button in the footer." },
      ],
    },
  },
  cookiesBanner: {
    title: "Your privacy matters",
    text: "We only use cookies essential to the site's operation. Non-essential cookies are activated only with your consent.",
    accept: "Accept all", reject: "Reject", customize: "Customise", save: "Save",
    essential: "Essential (always on)", analytics: "Analytics", marketing: "Marketing",
    manage: "Cookies",
  },
  breadcrumbHome: "Home",
  notFound: { title: "Page not found", text: "The page you requested does not exist or has been moved.", cta: "Back to home" },
  admin: {
    login: "Administration sign-in", email: "Email", password: "Password", signIn: "Sign in",
    dashboard: "Dashboard", leads: "Leads", coverage: "Coverage checks", quotes: "Quote requests",
    messages: "Messages", applications: "Applications", newsletter: "Newsletter",
    content: "Content", posts: "News", jobs: "Job openings", partners: "Partners", team: "Team",
    faqs: "FAQs", documents: "Documents", locations: "Offices", settings: "Settings", users: "Users",
    audit: "Audit log", outbox: "Email outbox", logout: "Sign out",
    statuses: { nouveau: "New", a_contacter: "To contact", contacte: "Contacted", qualifie: "Qualified", etude_technique: "Technical study", devis_envoye: "Quote sent", gagne: "Won", perdu: "Lost", archive: "Archived" },
    exportCsv: "Export CSV", search: "Search…", filterStatus: "Status", filterSource: "Source",
    assign: "Assign to", addNote: "Add a note", notes: "Internal notes", history: "History",
    save: "Save", cancel: "Cancel", create: "Create", edit: "Edit", delete: "Delete",
    published: "Published", draft: "Draft", confirmDelete: "Confirm deletion?",
    smtpTitle: "SMTP configuration (outgoing email)", testEmail: "Internal notification email",
    noData: "No data.", loginFailed: "Invalid credentials.",
    roles: { super_admin: "Super administrator", editor: "Content editor", sales: "Sales", analyst: "Analyst (read-only)" },
    totalLeads: "Total leads", newLeads: "New leads", thisMonth: "This month", bySource: "By source",
  },
};
